import { useRef, useEffect, useState } from "react";
import { fetchHomePagePortfolio } from "../../services/api";

const Base_URL = process.env.REACT_APP_Base_URL;

const LABELS = {
  ja: { filter: "フィルター", geography: "地域", country: "国", reset: "リセット", all: "すべて", direct: "直接", funds: "基金" },
  en: { filter: "Filter", geography: "Geography", country: "Country", reset: "Reset", all: "All", direct: "Direct", funds: "Funds" },
};
const getLabels = (lang) => LABELS[lang] || LABELS["en"];

// Maps a canonical type key to the backend's actual string value per language
const TYPE_VALUE_MAP = {
  en: { All: "All", Direct: "Direct", Funds: "Fund" },
  ja: { All: "All", Direct: "直接", Funds: "基金" },
};
const getTypeValueMap = (lang) => TYPE_VALUE_MAP[lang] || TYPE_VALUE_MAP["en"];

const getImageUrl = (img, fallbackImg = null) => {
  if (img?.formats?.large?.url) return `${Base_URL}${img.formats.large.url}`;
  if (img?.formats?.medium?.url) return `${Base_URL}${img.formats.medium.url}`;
  if (img?.formats?.thumbnail?.url) return `${Base_URL}${img.formats.thumbnail.url}`;
  if (img?.url) return `${Base_URL}${img.url}`;

  if (fallbackImg?.formats?.large?.url) return `${Base_URL}${fallbackImg.formats.large.url}`;
  if (fallbackImg?.formats?.medium?.url) return `${Base_URL}${fallbackImg.formats.medium.url}`;
  if (fallbackImg?.formats?.thumbnail?.url) return `${Base_URL}${fallbackImg.formats.thumbnail.url}`;
  if (fallbackImg?.url) return `${Base_URL}${fallbackImg.url}`;

  return "/fallback.jpg";
};

// ─── Custom Dropdown ──────────────────────────────────────────────────────────
function CustomDropdown({ label, options, value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selectedOption = options.find((opt) => opt.value === value);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="postfoliofilter-geo-country appearance-none flex items-center gap-2"
      >
        <span>{selectedOption ? selectedOption.label : label}</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="10" height="10" viewBox="0 0 10 10" fill="none"
          style={{ transition: "transform 0.2s", transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
        >
          <path d="M0.35352 4.71447L4.63965 9.00059L8.92578 4.71447M4.63965 9.00059V0" stroke="#004098"/>
        </svg>
      </button>

      {open && (
        <div className="absolute top-full mt-1 z-50 rounded-[10px] respo-filter-width min-w-[184px] py-2" style={{ backgroundColor: "#BCE2F5" }}>
          {options.map((opt) => (
            <label
              key={opt.value}
              className="flex items-center justify-between px-4 py-2 cursor-pointer text-[#004098]"
              style={{ gap: "20px" }}
            >
              <span>{opt.label}</span>
              <input
                type="radio"
                name={label}
                value={opt.value}
                checked={value === opt.value}
                onChange={() => { onChange(opt.value); setOpen(false); }}
                className="accent-[#004098]"
              />
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Filter Bar ───────────────────────────────────────────────────────────────
function FilterBar({ selectedType, onTypeChange, onReset, lang }) {
  const L = getLabels(lang);
  // value here is the canonical key ("All" / "Direct" / "Funds"), not the backend string
  const typeOptions = [
    { label: L.all, value: "All" },
    { label: L.direct, value: "Direct" },
    { label: L.funds, value: "Funds" },
  ];
  const hasFilter = selectedType && selectedType !== "All";

  return (
    <div className="flex items-center mb-8 flex-wrap mt-160 mb-[40px] g-30">
      <div className="portfolio-filter-dropdown">
        <span className="hidden xl:block">{L.filter}</span>
        <svg xmlns="http://www.w3.org/2000/svg" width="17" height="12" viewBox="0 0 17 12" fill="none">
          <path d="M0 0.75C0 0.551088 0.0790175 0.360322 0.21967 0.21967C0.360322 0.0790175 0.551088 0 0.75 0H15.75C15.9489 0 16.1397 0.0790175 16.2803 0.21967C16.421 0.360322 16.5 0.551088 16.5 0.75C16.5 0.948912 16.421 1.13968 16.2803 1.28033C16.1397 1.42098 15.9489 1.5 15.75 1.5H0.75C0.551088 1.5 0.360322 1.42098 0.21967 1.28033C0.0790175 1.13968 0 0.948912 0 0.75ZM2.5 5.75C2.5 5.55109 2.57902 5.36032 2.71967 5.21967C2.86032 5.07902 3.05109 5 3.25 5H13.25C13.4489 5 13.6397 5.07902 13.7803 5.21967C13.921 5.36032 14 5.55109 14 5.75C14 5.94891 13.921 6.13968 13.7803 6.28033C13.6397 6.42098 13.4489 6.5 13.25 6.5H3.25C3.05109 6.5 2.86032 6.42098 2.71967 6.28033C2.57902 6.13968 2.5 5.94891 2.5 5.75ZM5.5 10.75C5.5 10.5511 5.57902 10.3603 5.71967 10.2197C5.86032 10.079 6.05109 10 6.25 10H10.25C10.4489 10 10.6397 10.079 10.7803 10.2197C10.921 10.3603 11 10.5511 11 10.75C11 10.9489 10.921 11.1397 10.7803 11.2803C10.6397 11.421 10.4489 11.5 10.25 11.5H6.25C6.05109 11.5 5.86032 11.421 5.71967 11.2803C5.57902 11.1397 5.5 10.9489 5.5 10.75Z" fill="#BCE2F5"/>
        </svg>
      </div>

      <CustomDropdown
        label={L.filter}
        options={typeOptions}
        value={selectedType}
        onChange={onTypeChange}
      />

      {hasFilter && (
        <button onClick={onReset} className="text-accent-blue font-medium underline pt-[2px]">
          {L.reset}
        </button>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function HomePortfolio({ lang = "en", limit = 20, showLink }) {
  const [data, setData] = useState(null);
  const [fallbackData, setFallbackData] = useState(null);
  const [selectedType, setSelectedType] = useState("All"); // canonical key, language-independent

  const containerRefs = useRef([]);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 1024);

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth > 1024);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setData(null);

    fetchHomePagePortfolio("en")
      .then((res) => {
        if (res?.length) setFallbackData(res);
      })
      .catch(() => {});

    if (lang !== "en") {
      fetchHomePagePortfolio(lang)
        .then((res) => {
          if (res?.length) setData(res);
        })
        .catch(() => {});
    }
  }, [lang, limit]);

  const display = (fallbackData || []).map((fallbackItem, index) => {
    const localizedItem = data?.[index];
    return localizedItem && Object.keys(localizedItem).length > 0
      ? localizedItem
      : fallbackItem;
  });

  if (!display.length) return <p className="p-6">Loading...</p>;

  const typeValueMap = getTypeValueMap(lang);

  // Compares the item's own (localized) Type against the backend string
  // that corresponds to the selected canonical key, in the current language
  const matchesType = (item) => {
    if (selectedType === "All") return true;
    const expectedValue = typeValueMap[selectedType];
    const itemType = (item?.Type || "").trim();
    return itemType === expectedValue;
  };

  return (
    <div className="padding-x-40 padding-y-80 our-portfolio-home">
      {display.map((portfolio, portfolioIndex) => {
        const fallbackCompanies = fallbackData?.[portfolioIndex]?.company_details || [];

        const companies = fallbackCompanies.map((fallbackItem, index) => {
          const localizedItem = portfolio?.company_details?.[index];
          const merged =
            localizedItem && Object.keys(localizedItem).length > 0
              ? localizedItem
              : fallbackItem;
          return {
            ...merged,
            // Use the localized Type when available, since it's now translated;
            // fall back to the English Type only if localized data is missing
            Type: merged?.Type || fallbackItem?.Type || "",
            _fallback: fallbackItem,
          };
        });

        const filteredCompanies = companies.filter((item) => matchesType(item));
        const finalCompanies = limit
          ? filteredCompanies.slice(0, limit)
          : filteredCompanies;

        return (
          <div key={portfolio.id || portfolioIndex}>
            {/* Section Title */}
            <div className="section-name-wrapper">
              <div className="section-name-dot"></div>
              <h6 className="section-name">
                {portfolio.portfolio_section_name ||
                  fallbackData?.[portfolioIndex]?.portfolio_section_name || ""}
              </h6>
            </div>

            {/* Section Heading */}
            <h3 className="size-h3 col-xl-9 pt-2.5 text-dark-blue-color">
              {portfolio.portfolio_section_heading ||
                fallbackData?.[portfolioIndex]?.portfolio_section_heading || ""}
            </h3>

            {/* Filter Bar */}
            <FilterBar
              selectedType={selectedType}
              onTypeChange={setSelectedType}
              onReset={() => setSelectedType("All")}
              lang={lang}
            />

            {/* No Data Message OR Company Cards */}
            {finalCompanies.length === 0 ? (
              <div className="text-center py-16 text-gray-400 text-sm">
                No companies found for the selected filter.
              </div>
            ) : (
              <div className="custom-lg-flex flex flex-wrap items-start gap-x-[2%] gap-y-0">
                {finalCompanies.map((item, index) => {
                  const fallbackItem = item._fallback;
                  return (
                    <div
                      key={item.id || index}
                      className="w-[49%] md:w-[32%] xl:w-[23.5%] 3xl:w-[15%] flex flex-wrap items-end justify-center pb-5 md:pb-10"
                      ref={(el) => (containerRefs.current[index] = el)}
                    >
                      <div className="w-full relative group">
                        <div className="portfolio-image-wrapper" style={{ padding: "0 20px" }}>
                          <img
                            className="h-[auto] lg:h-[60px] greyscale-img"
                            style={{ width: "auto" }}
                            src={getImageUrl(item.company_image, fallbackItem?.company_image)}
                            alt={item.company_name || fallbackItem?.company_name || "Company image"}
                          />
                        </div>

                        <div className="absolute top-[90%] left-0 w-full px-[20px] rounded-[10px] z-[30] bg-[rgb(188,226,245)] max-h-0 overflow-hidden transition-all duration-500 delay-100 group-hover:max-h-[500px]">
                          <div className="py-[20px]">
                            <h5 className="font-medium text-dark-blue-color pt-[20px]">
                              {item.company_description || fallbackItem?.company_description || ""}
                            </h5>
                            <a
                              className="font-medium text-accent-blue py-[20px] block underline"
                            target="_blank"  href={item.company_link_url || fallbackItem?.company_link_url || "#"}
                            >
                              {item.company_link_text || fallbackItem?.company_link_text || ""}
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {showLink && (
              <a
                className="text-link block mt-5 break-words"
                style={{ display: "block" }}
                href={
                  portfolio?.portfolio_link_url ||
                  fallbackData?.[portfolioIndex]?.portfolio_link_url || "#"
                }
              >
                {portfolio?.portfolio_link_text ||
                  fallbackData?.[portfolioIndex]?.portfolio_link_text || ""}
              </a>
            )}
          </div>
        );
      })}
    </div>
  );
}
